import {
  CurrentReceiptData,
  CurrentReceiptItem,
  ReceiptsResponseData,
} from './types';

// Хелпер для рассчета максимальной скидки относительно суммы товаров и items_cover_percent

export const formatReceipt = (receipts: ReceiptsResponseData) => {
  if (!receipts?.checks?.length) return receipts;

  const recieptsArr = receipts.checks.map((receipt: CurrentReceiptData) => {
    if (!receipt?.items?.length) return receipt;

    // 1) Сумма чеков и максимально допустимая скидка (с учетом допустимой скидки аптеки)
    const itemsSum = receipt.items.reduce(
      (acc, item) => acc + (item.total_price || 0),
      0
    );
    const maxDiscount = Math.floor(
      (itemsSum / 100) * (receipt?.items_cover_percent || 1)
    );

    let remaining = maxDiscount; // // общий неистраченный остаток

    // копия товаров с базовой макс скидкой
    // percentToClient: если с бека приходит скидка по товару юзаем ее иначе используем дефолт скидку по чеку
    const items = receipt.items.map((item) => {
      return {
        ...item,
        maxBonusDiscount: item.maxBonusDiscount || 0,
        percentToClient: item.bonus_percent ?? receipt.items_check_percent,
        percentToPharmacist: receipt.pharmacist_check_percent,
        stadaPercent: item.stada_bonus_percent ?? 0,
      };
    });

    const shouldNotContinue = (items: CurrentReceiptItem[]) =>
      items.every((item) => {
        const maxAllowedDiscount =
          (item.total_price || 0) - (item.quantity || 0);

        return (
          item.maxBonusDiscount >= maxAllowedDiscount ||
          item.quantity > remaining
        );
      });

    const updateItems = (items: CurrentReceiptItem[]) =>
      items.map((item) => {
        const maxAllowedDiscount =
          (item.total_price || 0) - (item.quantity || 0);

        // Проверяем что еще можем списать по товару
        if (
          item.quantity <= remaining &&
          item.base_price >= item.quantity &&
          item.maxBonusDiscount + item.quantity <= maxAllowedDiscount
        ) {
          // если можем, увеличиваем макс искаунт и уменьшаем остаток
          remaining -= item.quantity;
          return {
            ...item,
            maxBonusDiscount: item.maxBonusDiscount + item.quantity,
          };
        }
        // если не можем, возвращаем товар как есть
        return item;
      });

    // проходимся по всем товарам и стараемся максимально израсходовать остаток
    let updatedItems = items;

    while (remaining > 0) {
      updatedItems = updateItems(updatedItems);

      // смотрим можем ли пройтись еще раз
      if (remaining === 0 || shouldNotContinue(updatedItems)) {
        // если не можем, останавливаемся
        break;
      }
    }

    // 4) Возвращаем ту же структуру, но с обновлёнными items
    return {
      ...receipt,
      items: updatedItems,
    };
  });

  return { checks: recieptsArr };
};

// округление вниз до кратности quantity
const floorToMultiple = (value: number, multiple: number) => {
  if (!multiple || multiple <= 0) return Math.floor(value);
  return Math.floor(value / multiple) * multiple;
};

// Хелпер для рассчета максимальной скидки с учетом бонусов клиента
export function distributeClientBonus(
  items: CurrentReceiptItem[],
  clientBonusCount: number,
  networkBonus?: number,
  stadaBonusCount: number = 0
): CurrentReceiptItem[] {
  if (!items?.length) return items;

  const clientBonus =
    clientBonusCount - (clientBonusCount % (networkBonus ?? 1));

  const stadaBonus = stadaBonusCount - (stadaBonusCount % (networkBonus ?? 1));

  const bonusesToReceive = (
    it: CurrentReceiptItem,
    bonusCount: number,
    percent: number
  ) => Math.round(((it.total_price - bonusCount) / 100) * percent);

  if (!clientBonus && !stadaBonus)
    return items.map((it) => ({
      ...it,
      appliedBonus: 0,
      appliedStadaBonus: 0,
      bonusesToClient: bonusesToReceive(it, 0, it.percentToClient),
      bonusesToPharmacist: bonusesToReceive(it, 0, it.percentToPharmacist),
      stadaBonus: bonusesToReceive(it, 0, it.stadaPercent),
    }));

  const count = items.length;
  const baseShare = clientBonus / count;

  // рабочая копия — cap сразу обрезается до кратного networkBonus,
  // чтобы суммарное списание по всем товарам гарантированно было кратно шагу
  const step = networkBonus ?? 1;
  const work = items.map((item) => ({
    ...item,
    appliedWork: 0,
    appliedStadaWork: 0,
    capByItem: floorToMultiple(
      Math.min(
        item.maxBonusDiscount != null
          ? item.maxBonusDiscount
          : item.total_price,
        item.total_price - Math.max(1, item.quantity || 1)
      ),
      step
    ),
  }));

  // Шаг 1: штадовские бонусы — максимально в штада-товары, остаток игнорируем
  if (stadaBonus > 0) {
    const stadaItems = work.filter((w) => (w.stadaPercent ?? 0) > 0);
    if (stadaItems.length > 0) {
      const stadaCount = stadaItems.length;
      const stadaBaseShare = stadaBonus / stadaCount;
      let stadaRemaining = stadaBonus;
      let stadaCarry = 0;

      // Первый проход по штада-товарам
      for (let i = 0; i < stadaItems.length; i += 1) {
        const w = stadaItems[i];
        const q = Math.max(1, w.quantity || 1);
        const allowedShare = stadaBaseShare + stadaCarry;
        const rawAlloc = Math.min(allowedShare, w.capByItem, stadaRemaining);
        const alloc = floorToMultiple(rawAlloc, q);
        w.appliedWork += alloc;
        stadaRemaining -= alloc;
        stadaCarry = Math.max(0, allowedShare - alloc);
      }

      // Второй проход штада: докрутка остатка
      if (stadaRemaining > 0) {
        const orderIdx = [...Array(stadaItems.length).keys()].sort(
          (a, b) =>
            (stadaItems[a].quantity || 1) - (stadaItems[b].quantity || 1)
        );
        let progress = true;
        while (stadaRemaining > 0 && progress) {
          progress = false;
          for (let k = 0; k < orderIdx.length; k += 1) {
            const idx = orderIdx[k];
            const w = stadaItems[idx];
            const q = Math.max(1, w.quantity || 1);
            if (stadaRemaining >= q) {
              const room = w.capByItem - w.appliedWork;
              if (room >= q) {
                w.appliedWork += q;
                stadaRemaining -= q;
                progress = true;
                if (stadaRemaining <= 0) break;
              }
            }
          }
        }
      }

      // Переносим appliedWork → appliedStadaWork, уменьшаем cap для аптечного пула
      stadaItems.forEach((w) => {
        w.appliedStadaWork = w.appliedWork;
        w.appliedWork = 0;
        w.capByItem = Math.max(0, w.capByItem - w.appliedStadaWork);
      });
    }
  }

  // === Шаг 2: аптечные бонусы — оригинальный алгоритм по всем товарам ===
  let remaining = clientBonus;
  let carry = 0;

  // === Первый проход: равные доли с округлением до кратности quantity ===
  for (let i = 0; i < work.length; i += 1) {
    const w = work[i];
    const q = Math.max(1, w.quantity || 1);

    const allowedShare = baseShare + carry;
    const rawAlloc = Math.min(allowedShare, w.capByItem, remaining);

    // округляем до кратности quantity, чтобы на единицу было целое число бонусов
    const alloc = floorToMultiple(rawAlloc, q);

    w.appliedWork = alloc;
    remaining -= alloc;
    carry = Math.max(0, allowedShare - alloc);
  }

  // === Второй проход: «докрутка» остатка пакетами кратными quantity ===
  if (remaining > 0) {
    // сортируем по возрастанию кратности для большей эффективности
    const orderIdx = [...Array(work.length).keys()].sort(
      (a, b) => (work[a].quantity || 1) - (work[b].quantity || 1)
    );

    let progress = true;
    while (remaining > 0 && progress) {
      progress = false;

      for (let k = 0; k < orderIdx.length; k += 1) {
        const idx = orderIdx[k];
        const w = work[idx];
        const q = Math.max(1, w.quantity || 1);

        // проверяем, хватает ли остатка и лимита хотя бы на один «пакет»
        if (remaining >= q) {
          const room = w.capByItem - w.appliedWork;
          if (room >= q) {
            // применяем ровно один пакет
            w.appliedWork += q;
            remaining -= q;
            progress = true;

            if (remaining <= 0) break;
          }
        }
      }
    }
  }

  return work.map((w) => {
    const maxAppliedBonus = Math.floor(w.appliedWork);
    const appliedBonus =
      maxAppliedBonus === w.total_price
        ? maxAppliedBonus - w.quantity * (networkBonus ?? 1)
        : maxAppliedBonus;

    const maxStadaBonus = Math.floor(w.appliedStadaWork);
    const appliedStadaBonus =
      maxStadaBonus === w.total_price
        ? maxStadaBonus - w.quantity
        : maxStadaBonus;

    const totalApplied = appliedBonus + appliedStadaBonus;

    return {
      ...w,
      appliedBonus: totalApplied,
      appliedStadaBonus,
      bonusesToClient: bonusesToReceive(w, totalApplied, w.percentToClient),
      bonusesToPharmacist: bonusesToReceive(
        w,
        totalApplied,
        w.percentToPharmacist
      ),
      stadaBonus: bonusesToReceive(w, totalApplied, w.stadaPercent),
    } as CurrentReceiptItem;
  });
}

export const formatDataForLoyaltyReport = (
  startDay: Date,
  endDay: Date,
  integrationCode: string,
  source?: string,
  phone?: string
) => {
  const getDateString = (st: Date) => {
    const formatedStr = st
      .toLocaleString()
      .replace(/[a-zа-яё]/gi, '')
      .trim();
    const ar = formatedStr.split(', ');

    const month =
      st.getMonth() + 1 > 9 ? st.getMonth() + 1 : `0${st.getMonth() + 1}`;
    const day = st.getDate() > 9 ? st.getDate() : `0${st.getDate()}`;
    const year = st.getFullYear();
    const getGMT = st.toString().split('GMT')[1].split(' ')[0].slice(1, 3);

    return getGMT === '00'
      ? `${year}-${month}-${day}T${ar[1]}Z`
      : `${year}-${month}-${day}T${ar[1]}+${getGMT}:00`;
  };

  const sourceCode = source ? { source_code: source } : {};
  const phoneCode = phone ? { phone } : {};

  return {
    from: getDateString(startDay),
    to: getDateString(endDay),
    type: 'pharmacy',
    integration_code: integrationCode,
    ...sourceCode,
    ...phoneCode,
  };
};
